import { KeywordAutomaton, type KeywordCandidate, type KeywordEntry } from "./keyword-automaton";
import type { MatchRule } from "../types";
import { isWholeKeywordMatch } from "../shared/keyword-boundaries";

interface Interval {
  start: number;
  end: number;
}

interface BaseCompiledRule {
  id: string;
  order: number;
  maxMatches: number;
}

interface CompiledKeywordRule extends BaseCompiledRule {
  type: "keyword";
  wholeWord: boolean;
  sourcePatterns: string[];
}

interface CompiledRegexRule extends BaseCompiledRule {
  type: "regex";
  regex: RegExp;
  sourcePattern: string;
}

export type CompiledRule = CompiledKeywordRule | CompiledRegexRule;

export interface TextMatch {
  rule: CompiledRule;
  start: number;
  end: number;
  text: string;
  normalizedMatch: string;
  sourcePattern: string;
}

export interface CompiledMatcher {
  rules: CompiledRule[];
  keywordAutomaton: KeywordAutomaton;
}

interface Candidate {
  start: number;
  end: number;
  text: string;
  normalizedMatch: string;
  sourcePattern: string;
}

export function compileMatcher(rules: MatchRule[], defaultMaxMatches: number): CompiledMatcher {
  const compiled: CompiledRule[] = [];
  const keywordEntries: KeywordEntry[] = [];

  rules.forEach((rule, order) => {
    const maxMatches = rule.maxMatches ?? defaultMaxMatches;
    if (!Number.isFinite(maxMatches) || maxMatches < 1) {
      throw new Error(`Invalid maxMatches for rule \"${rule.id}\".`);
    }

    if (rule.type === "keyword") {
      const keywords = Array.isArray(rule.pattern) ? rule.pattern : [rule.pattern];
      const normalizedKeywords = keywords
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0);

      if (normalizedKeywords.length === 0) {
        throw new Error(`Keyword rule \"${rule.id}\" has no usable patterns.`);
      }

      compiled.push({
        id: rule.id,
        type: "keyword",
        order,
        maxMatches,
        wholeWord: rule.wholeWord ?? true,
        sourcePatterns: normalizedKeywords
      });

      normalizedKeywords.forEach((keyword) => {
        keywordEntries.push({
          ruleId: rule.id,
          ruleOrder: order,
          keyword,
          sourcePattern: keyword
        });
      });
      return;
    }

    const rawFlags = rule.flags ?? "";
    const ensuredFlags = rawFlags.includes("g") ? rawFlags : `${rawFlags}g`;
    let compiledRegex: RegExp;
    try {
      compiledRegex = new RegExp(rule.pattern, ensuredFlags);
    } catch (error) {
      throw new Error(`Regex rule \"${rule.id}\" is invalid: ${(error as Error).message}`);
    }

    compiled.push({
      id: rule.id,
      type: "regex",
      order,
      maxMatches,
      regex: compiledRegex,
      sourcePattern: rule.pattern
    });
  });

  return {
    rules: compiled,
    keywordAutomaton: new KeywordAutomaton(keywordEntries)
  };
}

export function findMatchesInText(
  text: string,
  matcher: CompiledMatcher,
  usageByRule: Map<string, number>
): TextMatch[] {
  if (text.length === 0) {
    return [];
  }

  const candidatesByRule = new Map<string, Candidate[]>();

  const keywordCandidates = matcher.keywordAutomaton.search(text);
  for (const candidate of keywordCandidates) {
    pushCandidate(candidatesByRule, candidate.ruleId, {
      start: candidate.start,
      end: candidate.end,
      text: candidate.text,
      normalizedMatch: candidate.normalizedMatch,
      sourcePattern: candidate.sourcePattern
    });
  }

  for (const rule of matcher.rules) {
    if (rule.type !== "regex") {
      continue;
    }

    rule.regex.lastIndex = 0;
    let safetyCounter = 0;

    while (safetyCounter < text.length + 1) {
      safetyCounter += 1;
      const result = rule.regex.exec(text);
      if (!result) {
        break;
      }

      const matchText = result[0];
      if (matchText.length === 0) {
        rule.regex.lastIndex += 1;
        continue;
      }

      const start = result.index;
      const end = start + matchText.length;
      pushCandidate(candidatesByRule, rule.id, {
        start,
        end,
        text: matchText,
        normalizedMatch: matchText.toLocaleLowerCase(),
        sourcePattern: rule.sourcePattern
      });
    }
  }

  const accepted: TextMatch[] = [];
  const occupiedIntervals: Interval[] = [];

  for (const rule of matcher.rules) {
    let currentCount = usageByRule.get(rule.id) ?? 0;
    if (currentCount >= rule.maxMatches) {
      continue;
    }

    const candidates = (candidatesByRule.get(rule.id) ?? []).sort((left, right) => {
      if (left.start !== right.start) {
        return left.start - right.start;
      }
      return right.end - left.end;
    });

    for (const candidate of candidates) {
      if (currentCount >= rule.maxMatches) {
        break;
      }

      if (rule.type === "keyword" && rule.wholeWord && !isWholeKeywordMatch(text, candidate.start, candidate.end)) {
        continue;
      }

      if (hasOverlap(occupiedIntervals, candidate.start, candidate.end)) {
        continue;
      }

      insertInterval(occupiedIntervals, candidate.start, candidate.end);
      accepted.push({
        rule,
        start: candidate.start,
        end: candidate.end,
        text: candidate.text,
        normalizedMatch: candidate.normalizedMatch,
        sourcePattern: candidate.sourcePattern
      });
      currentCount += 1;
      usageByRule.set(rule.id, currentCount);
    }
  }

  return accepted.sort((left, right) => left.start - right.start);
}

function pushCandidate(target: Map<string, Candidate[]>, ruleId: string, candidate: Candidate): void {
  const bucket = target.get(ruleId);
  if (bucket) {
    bucket.push(candidate);
    return;
  }
  target.set(ruleId, [candidate]);
}

function hasOverlap(intervals: Interval[], start: number, end: number): boolean {
  const index = findInsertionIndex(intervals, start);

  if (index > 0 && intervals[index - 1].end > start) {
    return true;
  }

  if (index < intervals.length && intervals[index].start < end) {
    return true;
  }

  return false;
}

function insertInterval(intervals: Interval[], start: number, end: number): void {
  const index = findInsertionIndex(intervals, start);
  intervals.splice(index, 0, { start, end });
}

function findInsertionIndex(intervals: Interval[], start: number): number {
  let low = 0;
  let high = intervals.length;

  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (intervals[middle].start < start) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
}
